import { Modal } from '@components/common/modal/modal';
import { ReactElement, useCallback, useEffect, useRef, useState } from 'react';

export interface KycDocumentCameraModalProps {
	isOpen: boolean;
	stream: MediaStream | null;
	isCameraLoading: boolean;
	onRequestCamera: () => void | Promise<void>;
	onClose: () => void;
	onCapture: (file: File) => void;
}

const ShieldIcon = (): ReactElement => (
	<svg className="kyc-document-camera-modal__shield" viewBox="0 0 24 24" width="22" height="22" aria-hidden>
		<path
			fill="none"
			stroke="currentColor"
			strokeWidth="1.4"
			strokeLinejoin="round"
			d="M12 3.5l7 3.2v5.2c0 4.5-3 8.7-7 9.6-4-1-7-5.1-7-9.6V6.7l7-3.2z"
		/>
	</svg>
);

export const KycDocumentCameraModal = (props: KycDocumentCameraModalProps): ReactElement => {
	const { isOpen, stream, isCameraLoading, onRequestCamera, onClose, onCapture } = props;
	const videoRef = useRef<HTMLVideoElement>(null);
	const canvasRef = useRef<HTMLCanvasElement>(null);
	const [rotationDeg, setRotationDeg] = useState(0);

	useEffect(() => {
		if (!isOpen) {
			setRotationDeg(0);
		}
	}, [isOpen]);

	useEffect(() => {
		const video = videoRef.current;
		if (!isOpen || !stream || !video) {
			return;
		}
		video.srcObject = stream;
		void video.play().catch(() => undefined);
		return (): void => {
			video.srcObject = null;
		};
	}, [isOpen, stream]);

	const rotateLeft = useCallback((): void => {
		setRotationDeg((d) => (d - 90 + 360) % 360);
	}, []);

	const rotateRight = useCallback((): void => {
		setRotationDeg((d) => (d + 90) % 360);
	}, []);

	const handleCapture = useCallback((): void => {
		const video = videoRef.current;
		const canvas = canvasRef.current;
		if (!video || !canvas || video.readyState < 2) {
			return;
		}
		const srcW = video.videoWidth;
		const srcH = video.videoHeight;
		if (!srcW || !srcH) {
			return;
		}
		const rad = (rotationDeg * Math.PI) / 180;
		const swap = rotationDeg === 90 || rotationDeg === 270;
		const outW = swap ? srcH : srcW;
		const outH = swap ? srcW : srcH;
		canvas.width = outW;
		canvas.height = outH;
		const ctx = canvas.getContext('2d');
		if (!ctx) {
			return;
		}
		ctx.translate(outW / 2, outH / 2);
		ctx.rotate(rad);
		ctx.drawImage(video, -srcW / 2, -srcH / 2);
		canvas.toBlob(
			(blob) => {
				if (!blob) {
					return;
				}
				const file = new File([blob], `kyc-document-${Date.now()}.jpg`, { type: 'image/jpeg' });
				onCapture(file);
			},
			'image/jpeg',
			0.92
		);
	}, [onCapture, rotationDeg]);

	const showPermission = !stream;

	return (
		<Modal
			modalOpen={isOpen}
			modalId="kyc-document-camera"
			onModalClose={onClose}
			className="dialog-md kyc-document-camera-modal"
			modalTitle="Take document photo"
		>
			<div className="kyc-document-camera-modal__body">
				<div className="kyc-document-camera-modal__stage">
					<div className="kyc-document-camera-modal__oval">
						<div className="kyc-document-camera-modal__oval-video" style={{ transform: `rotate(${rotationDeg}deg)` }}>
							<video
								ref={videoRef}
								className="kyc-document-camera-modal__video"
								playsInline
								muted
								autoPlay
								hidden={!stream}
							/>
						</div>
						{showPermission ? (
							<div className="kyc-document-camera-modal__permission">
								<p className="kyc-document-camera-modal__permission-text">
									Camera access is needed to take a document photo.
								</p>
								<button
									type="button"
									className="kyc-document-camera-modal__allow-btn"
									disabled={isCameraLoading}
									onClick={() => {
										void onRequestCamera();
									}}
								>
									{isCameraLoading ? 'Opening camera…' : 'Allow camera'}
								</button>
							</div>
						) : null}
					</div>
				</div>

				<div className="kyc-document-camera-modal__hint" role="note">
					<ShieldIcon />
					<p>Make sure you&apos;re in a place with enough light to take a clear photo of your document.</p>
				</div>

				<div className="kyc-document-camera-modal__footer">
					<div className="kyc-document-camera-modal__rotate-bar" aria-label="Rotate preview">
						<button
							type="button"
							className="kyc-document-camera-modal__rotate-btn"
							onClick={rotateLeft}
							disabled={!stream}
							aria-label="Rotate counter-clockwise"
						>
							<span className="kyc-document-camera-modal__rotate-icon" aria-hidden>
								↶
							</span>
						</button>
						<button
							type="button"
							className="kyc-document-camera-modal__rotate-btn"
							onClick={rotateRight}
							disabled={!stream}
							aria-label="Rotate clockwise"
						>
							<span className="kyc-document-camera-modal__rotate-icon" aria-hidden>
								↷
							</span>
						</button>
					</div>
					<div className="kyc-document-camera-modal__actions">
						<button
							type="button"
							className="kyc-document-camera-modal__btn kyc-document-camera-modal__btn--ghost"
							onClick={onClose}
						>
							Cancel
						</button>
						<button
							type="button"
							className="kyc-document-camera-modal__btn kyc-document-camera-modal__btn--primary"
							onClick={handleCapture}
							disabled={!stream}
						>
							Use photo
						</button>
					</div>
				</div>
				<canvas ref={canvasRef} className="kyc-document-camera-modal__canvas" aria-hidden />
			</div>
		</Modal>
	);
};
